import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  TextField,
  Select,
  MenuItem,
  Autocomplete,
  CircularProgress,
  FormControl, 
  InputLabel,
} from "@mui/material";
import { Formik } from "formik";
import * as yup from "yup";
import Swal from "sweetalert2";
import useMediaQuery from "@mui/material/useMediaQuery";
import {
  generateAndExecuteMutation,
  graphqlQuery,
} from "../../../../data/Axios/DynamicService";
import {
  HRService,
  setupManagementUrl,
  companyManagementUrl,
} from "../../../../config";
// Import updated queries
import { allCompanies, allCountries } from "../../../../data/Axios/queries";
import { 
  AllCalculationType,
  AllStatutoryComponent,
  PayComponent,
} from "../../../../data/Queries/HRmanagement/leavemanagement";
import Header from "../../../../components/Header";

const base_url = HRService.uri;
const client_baseurl = companyManagementUrl.uri;
const setupmanagement_baseurl = setupManagementUrl.uri;

const PayComponentForm = (props) => {
  const [companiesData, setCompaniesData] = useState([]);
  const [countriesData, setCountriesData] = useState([]);
  const [componentTypeOptions, setComponentTypeOptions] = useState([]);
  const [calculationBasisOptions, setCalculationBasisOptions] = useState([]);
  const [statutoryComponentsData, setStatutoryComponentsData] = useState([]);
  const [loading, setLoading] = useState(true);

  const initialValues = {
    companyId: props.data ? props.data.companyId : "",
    countryId: props.data ? props.data.countryId : "",
    code: props.data ? props.data.code : "",
    name: props.data ? props.data.name : "",
    componentType: props.data ? props.data.componentType : "",
    isTaxable: props.data ? props.data.isTaxable : false,
    isPensionable: props.data ? props.data.isPensionable : false,
    isStatutory: props.data ? props.data.isStatutory : false,
    isRecurring: props.data ? props.data.isRecurring : false,
    isPreTax: props.data ? props.data.isPreTax : false,
    calculationBasis: props.data ? props.data.calculationBasis : "",
    defaultRate: props.data ? props.data.defaultRate : 0,
    formulaExpression: props.data ? props.data.formulaExpression : "",
    statutoryComponentId: props.data ? props.data.statutoryComponentId : null,
    sortOrder: props.data ? props.data.sortOrder : 0,
    isActive: props.data ? props.data.isActive : true,
  };

  console.log("Props Data:", props.data);

  const companyOptions = companiesData
    ? companiesData.map((data) => ({
        value: data.id,
        label: data.companyName || data.name,
      }))
    : [];

  const countryOptions = countriesData
    ? countriesData.map((data) => ({
        value: String (data.id),
        label: data.country || data.countryName,
      }))
    : [];

  const statutoryComponentOptions = statutoryComponentsData
    ? statutoryComponentsData.map((data) => ({
        value: data.id,
        label: data.name,
      }))
    : [];

  // Component type options (you might want to fetch these from an enum or config)
  const componentTypeOptionsData = [
    { value: "EARNING", label: "Earning" },
    { value: "DEDUCTION", label: "Deduction" },
  ];

  const decodedToken = JSON.parse(localStorage.getItem("decodedToken"));
  const userId = parseInt(decodedToken.Id);

  const validationSchema = yup.object().shape({
    code: yup.string().required("Code is required"),
    name: yup.string().required("Name is required"),
    componentType: yup.string().required("Component Type is required"),
    calculationBasis: yup.string().required("Calculation Basis is required"),
    defaultRate: yup.number().min(0, "Default Rate must be positive"),
    sortOrder: yup.number().min(0, "Sort Order must be non-negative"),
    companyId: yup.string().required("Company is required"),
    countryId: yup.string().required("County is required"),
  });

  const isNonMobile = useMediaQuery("(min-width:600px)");

  // Fetch companies
  useEffect(() => {
    async function fetchCompanies() {
      try {
        const data = await graphqlQuery(allCompanies, client_baseurl);
        setCompaniesData(data);
      } catch (error) {
        console.error("Error fetching companies:", error);
      }
    }
    fetchCompanies();
  }, []);

  // Fetch countries instead of countries
  useEffect(() => {
    async function fetchCountries() {
      try {
        const data = await graphqlQuery(allCountries, setupmanagement_baseurl);
        setCountriesData(data);
      } catch (error) {
        console.error("Error fetching countries:", error);
      }
    }
    fetchCountries();
  }, []);

  // Fetch statutory components
  useEffect(() => {
    async function fetchStatutoryComponents() {
      try {
        const data = await graphqlQuery(AllStatutoryComponent, base_url);
        setStatutoryComponentsData(data);
      } catch (error) {
        console.error("Error fetching statutory components:", error);
      }
    }
    fetchStatutoryComponents();
  }, []);

  // Fetch calculation basis
  useEffect(() => {
    async function fetchCalculationBasis() {
      try {
        const data = await graphqlQuery(AllCalculationType, base_url);
        setCalculationBasisOptions(data);
      } catch (error) {
        console.error("Error fetching calculation basis:", error);
      }
    }
    fetchCalculationBasis();
  }, []);

  // Set component types from static data
  useEffect(() => {
    setComponentTypeOptions(componentTypeOptionsData);
  }, []);

  // Set loading false after all fetches
   useEffect(() => {
    if (
      companiesData.length > 0 &&
      countriesData.length > 0 &&
      componentTypeOptions.length > 0 &&
      calculationBasisOptions.length > 0 &&
      statutoryComponentsData.length > 0
    ) {
      setLoading(false);
    }
  }, [
    companiesData,
    countriesData,
    componentTypeOptions,
    calculationBasisOptions,
    statutoryComponentsData,
  ]);
    

  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      const {
        companyId,
        countryId,
        code,
        name,
        componentType,
        isTaxable,
        isPensionable,
        isStatutory,
        isRecurring,
        isPreTax,
        calculationBasis,
        defaultRate,
        formulaExpression,
        statutoryComponentId,
        sortOrder,
        isActive,
      } = values;

      // helpers
      const toIntOrNull = (v) => {
        if (v === null || v === undefined || v === "") return null;
        const n = parseInt(v, 10);
        return Number.isFinite(n) ? n : null;
      };
      const toNumberOrNull = (v) => {
        if (v === null || v === undefined || v === "") return null;
        const n = Number(v);
        return Number.isFinite(n) ? n : null;
      };

      const parsedCompanyId = toIntOrNull(companyId);
      if (parsedCompanyId === null) {
        Swal.fire({ icon: "error", title: "Validation", text: "Please select a valid Company." });
        setSubmitting(false);
        return;
      }

      const parsedStatutoryId = toIntOrNull(statutoryComponentId);

      const parsedDefaultRate = 
        defaultRate === "" || defaultRate === null || defaultRate === undefined
        ? 0
        :Number(defaultRate);
        
      const parsedSortOrder = toIntOrNull(sortOrder) ?? 0;

      // build sanitized payload
      const basePayload = {
        companyId: parsedCompanyId,
        countryId: countryId != null ? String(countryId) : null,
        code,
        name,
        componentType,
        isTaxable: !!isTaxable,
        isPensionable: !!isPensionable,
        isStatutory: !!isStatutory,
        isRecurring: !!isRecurring,
        isPreTax: !!isPreTax,
        calculationBasis,
        defaultRate: parsedDefaultRate,
        formulaExpression,
        statutoryComponentId: parsedStatutoryId,
        sortOrder: parsedSortOrder,
        isActive: !!isActive,
      };

      const mutationData = props.isEditing
        ? { id: props.data?.id, ...basePayload, modifiedBy: userId }
        : { ...basePayload, createdBy: userId };

      console.log("Editing?", props.isEditing);
      console.log("Mutation data:", mutationData);

      let response;
      try {
        if (props.isEditing) {
          response = await generateAndExecuteMutation(
            "updatePayComponent",
            mutationData,
            "payComponent",
            base_url
          );
        } else {
          response = await generateAndExecuteMutation(
            "createPayComponent",
            mutationData,
            "payComponent",
            base_url
          );
        }
        console.log("GraphQL response:", response);
      } catch (gqlErr) {
        // surface GraphQL error details returned by axios
        console.error("GraphQL execution error:", gqlErr);
        console.error("GraphQL response data:", gqlErr?.response?.data || gqlErr);
        throw gqlErr;
      }

      if (response) {
        props.onAction();
        props.onClose();
        Swal.fire({
          icon: "success",
          title: "Success!",
          text: `Pay Component ${props.isEditing ? "updated" : "created"} successfully.`,
        });
      }
    } catch (error) {
      console.error("Error during form submission:", error);
      Swal.fire({
        icon: "error",
        title: "Error!",
        text: "An error occurred while processing your request. Please check console and backend logs.",
      });
    } finally {
      setSubmitting(false);
    }
  }

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box m="20px">
      <Header
        title={props.isEditing ? "Edit Pay Component" : "Create Pay Component"}
        subtitle={
          props.isEditing ? "Edit an Existing Pay Component" : "Create a New Pay Component"
        }
      />

      <Formik
        onSubmit={handleSubmit}
        initialValues={initialValues}
        validationSchema={validationSchema}
      >
        {({
          values,
          errors,
          touched,
          handleBlur,
          handleChange,
          handleSubmit,
          setFieldValue,
          isSubmitting,
        }) => (
          <form onSubmit={handleSubmit}>
            <Box
              display="grid"
              gap="30px"
              gridTemplateColumns="repeat(4, minmax(0, 1fr))"
              sx={{
                "& > div": {
                  gridColumn: isNonMobile ? undefined : "span 4",
                },
              }}
            >
              <TextField
                fullWidth
                variant="filled"
                type="text"
                label="Code *"
                onBlur={handleBlur}
                onChange={handleChange}
                value={values.code}
                name="code"
                error={touched.code && !!errors.code}
                helperText={touched.code && errors.code}
                sx={{ gridColumn: "span 2" }}
              />
              <TextField
                fullWidth
                variant="filled"
                type="text"
                label="Name *"
                onBlur={handleBlur}
                onChange={handleChange}
                value={values.name}
                name="name"
                error={touched.name && !!errors.name}
                helperText={touched.name && errors.name}
                sx={{ gridColumn: "span 2" }}
              />
              
              {/* Component Type */}
              <Select
                fullWidth
                variant="filled"
                value={values.componentType}
                name="componentType"
                onBlur={handleBlur}
                onChange={handleChange}
                error={touched.componentType && !!errors.componentType}
                sx={{ gridColumn: "span 2" }}
                displayEmpty
              >
                <MenuItem value="" disabled>
                  Select Component Type
                </MenuItem>
                {componentTypeOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>

              {/* Calculation Basis */}
              <Select
                fullWidth
                variant="filled"
                value={values.calculationBasis}
                name="calculationBasis"
                onBlur={handleBlur}
                onChange={handleChange}
                error={touched.calculationBasis && !!errors.calculationBasis}
                sx={{ gridColumn: "span 2" }}
                displayEmpty
              >
                <MenuItem value="" disabled>
                  Select Calculation Basis
                </MenuItem>
                {calculationBasisOptions.map((option) => (
                  <MenuItem key={option.id} value={String (option.id)}>
                    {option.name}
                  </MenuItem>
                ))}
              </Select>

              <TextField
                fullWidth
                variant="filled"
                type="number"
                label="Default Rate"
                onBlur={handleBlur}
                onChange={handleChange}
                value={values.defaultRate}
                name="defaultRate"
                error={touched.defaultRate && !!errors.defaultRate}
                helperText={touched.defaultRate && errors.defaultRate}
                sx={{ gridColumn: "span 1" }}
              />
              <TextField
                fullWidth
                variant="filled"
                type="number"
                label="Sort Order"
                onBlur={handleBlur}
                onChange={handleChange}
                value={values.sortOrder}
                name="sortOrder"
                error={touched.sortOrder && !!errors.sortOrder}
                helperText={touched.sortOrder && errors.sortOrder}
                sx={{ gridColumn: "span 1" }}
              />

              {/* Company */}
              <Autocomplete
                options={companyOptions}
                getOptionLabel={(option) => option.label}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Company *"
                    variant="filled"
                    error={touched.companyId && !!errors.companyId}
                    helperText={touched.companyId && errors.companyId}
                    onBlur={handleBlur}
                  />
                )}
                onChange={(event, newValue) => {
                  setFieldValue("companyId", newValue ? newValue.value : "");
                }}
                value={
                  companyOptions.find(
                    (option) => option.value === values.companyId
                  ) || null
                }
                sx={{ gridColumn: "span 2" }}
              />

              {/* County - Updated from Country */}
              <Autocomplete
                options={countryOptions}
                getOptionLabel={(option) => option.label}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Country *"
                    variant="filled"
                    error={touched.countryId && !!errors.countryId}
                    helperText={touched.countryId && errors.countryId}
                    onBlur={handleBlur}
                  />
                )}
                onChange={(event, newValue) => {
                  setFieldValue("countryId", newValue ? String (newValue.value) : "");
                }}
                value={
                  countryOptions.find(
                    (option) => option.value === values.countryId
                  ) || null
                }
                sx={{ gridColumn: "span 2" }}
              />

              {/* Statutory Component */}
              <Autocomplete
                options={statutoryComponentOptions}
                getOptionLabel={(option) => option.label}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Statutory Component"
                    variant="filled"
                    onBlur={handleBlur}
                  />
                )}
                onChange={(event, newValue) => {
                  setFieldValue("statutoryComponentId", newValue ? newValue.value : null);
                  setFieldValue("isStatutory", !!newValue);
                }}
                value={
                  statutoryComponentOptions.find(
                    (option) => option.value === values.statutoryComponentId
                  ) || null
                }
                sx={{ gridColumn: "span 2" }}
              />

              <TextField
                fullWidth
                variant="filled"
                type="text"
                label="Formula Expression"
                onBlur={handleBlur}
                onChange={handleChange}
                value={values.formulaExpression}
                name="formulaExpression"
                multiline
                rows={3}
                sx={{ gridColumn: "span 2" }}
              />

              {/* Boolean fields */}
              <FormControl variant="filled" fullWidth sx={{ gridColumn: "span 1" }}>
                  <InputLabel id="isTaxable-label">Is Taxable</InputLabel>
                  <Select
                    labelId="isTaxable-label"
                    value={values.isTaxable ? "Yes" : "No"}
                    onChange={(e) => setFieldValue("isTaxable", e.target.value === "Yes")}
                  >
                    <MenuItem value="Yes">Yes</MenuItem>
                    <MenuItem value="No">No</MenuItem>
                  </Select>
              </FormControl>
              
              <FormControl variant="filled" fullWidth sx={{ gridColumn: "span 1" }}>
                  <InputLabel id="isPensionable-label">Is Pensionable</InputLabel>
                  <Select
                    labelId="isPensionable-label"
                    value={values.isTaxable ? "Yes" : "No"}
                    onChange={(e) => setFieldValue("isPensionable", e.target.value === "Yes")}
                  >
                    <MenuItem value="Yes">Yes</MenuItem>
                    <MenuItem value="No">No</MenuItem>
                  </Select>
              </FormControl>

              <FormControl variant="filled" fullWidth sx={{ gridColumn: "span 1" }}>
                  <InputLabel id="isStatutory-label">Is Statutory</InputLabel>
                  <Select
                    labelId="isStatutory-label"
                    value={values.isTaxable ? "Yes" : "No"}
                    onChange={(e) => setFieldValue("isStatutory", e.target.value === "Yes")}
                  >
                    <MenuItem value="Yes">Yes</MenuItem>
                    <MenuItem value="No">No</MenuItem>
                  </Select>
              </FormControl>

              <FormControl variant="filled" fullWidth sx={{ gridColumn: "span 1" }}>
                  <InputLabel id="isRecurring-label">Is Recurring</InputLabel>
                  <Select
                    labelId="isRecurring-label"
                    value={values.isTaxable ? "Yes" : "No"}
                    onChange={(e) => setFieldValue("isRecurring", e.target.value === "Yes")}
                  >
                    <MenuItem value="Yes">Yes</MenuItem>
                    <MenuItem value="No">No</MenuItem>
                  </Select>
              </FormControl>

              <FormControl variant="filled" fullWidth sx={{ gridColumn: "span 1" }}>
                  <InputLabel id="isPreTax-label">Is PreTax</InputLabel>
                  <Select
                    labelId="isPreTax-label"
                    value={values.isTaxable ? "Yes" : "No"}
                    onChange={(e) => setFieldValue("isPreTax", e.target.value === "Yes")}
                  >
                    <MenuItem value="Yes">Yes</MenuItem>
                    <MenuItem value="No">No</MenuItem>
                  </Select>
              </FormControl>

              <FormControl variant="filled" fullWidth sx={{ gridColumn: "span 1" }}>
                  <InputLabel id="isActive-label">Is Active</InputLabel>
                  <Select
                    labelId="isActive-label"
                    value={values.isTaxable ? "Yes" : "No"}
                    onChange={(e) => setFieldValue("isActive", e.target.value === "Yes")}
                  >
                    <MenuItem value="Yes">Yes</MenuItem>
                    <MenuItem value="No">No</MenuItem>
                  </Select>
              </FormControl>

            </Box>
            <Box display="flex" justifyContent="end" mt="20px">
              <Button
                type="submit"
                color="secondary"
                variant="contained"
                disabled={isSubmitting}
              >
                {isSubmitting ? "Submitting..." : "Submit"}
              </Button>
            </Box>
          </form>
        )}
      </Formik>
    </Box>
  );
};

export default PayComponentForm;